import { jwtDecode } from 'jwt-decode';
import memoize from 'lodash/memoize';

import { type Signin, type RefreshTokens, AuthActionTypes } from 'actions/auth';
import createReducer from 'utils/create-reducer';
import type { ActionCreator, Handler } from 'actions';
import { setStorage, getStorage, removeStorage } from 'utils/storage';

type AuthHandler<Action extends ActionCreator> = Handler<AuthState, Action>;

type BaseAuthState = {
  authorization: string | null;
  expires: Date | null;
  signedIn: boolean;
  refreshToken: string | null;
  userId: number | null;
};

export type AuthState = BaseAuthState & {
  initialized: boolean;
  signoutTriggerBy: 'user' | 'system' | null;
};

const emptyState: BaseAuthState = {
  authorization: null,
  expires: null,
  signedIn: false,
  refreshToken: null,
  userId: null,
};

const AUTH_COOKIE_NAME = 'hyrox_authorization';
const AUTH_LS_EXPIRES = 'hyrox_authorization_expiry';
const REFRESH_LS_NAME = 'hyrox_authorization_refresh_token';

const setState: (state: AuthState) => void = ({ authorization, expires, refreshToken }: AuthState) => {
  if (authorization) {
    setStorage(AUTH_COOKIE_NAME, authorization, { domain: window.location.hostname, sameSite: 'None', secure: true });
  }
  if (expires) {
    localStorage.setItem(AUTH_LS_EXPIRES, String(expires));
  } else {
    localStorage.removeItem(AUTH_LS_EXPIRES);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_LS_NAME, refreshToken);
  }
};

type DecodedAuthToken = {
  user?: {
    id: string;
  };
  sub?: string;
};

export const extractUserIdFromToken = memoize((accessToken: string) => {
  const { user, sub } = jwtDecode<DecodedAuthToken>(accessToken);
  return Number.parseInt(user?.id ?? sub ?? '', 10);
});

const tokenTypeRegex = /^[^ ]* /;
export const extractUserIdFromAuth = memoize((authorization: string) =>
  extractUserIdFromToken(authorization.replace(tokenTypeRegex, '')),
);

const clearState: () => void = () => {
  removeStorage(AUTH_COOKIE_NAME, { domain: window.location.hostname, sameSite: 'None', secure: true });
  localStorage.removeItem(AUTH_LS_EXPIRES);
  localStorage.removeItem(REFRESH_LS_NAME);
  localStorage.clear();
};

// Read-only counterpart to getPersistedState, which calls clearState() — and therefore localStorage.clear()
// — whenever the session is missing or expired. Background readers that are not the app's auth lifecycle
// (the hyrox mirror's lookups) must never destroy the primary refresh token as a side effect of a read.
export const peekAuthorization = (): string | null => {
  const authorization = getStorage(AUTH_COOKIE_NAME) || null;

  if (!authorization) {
    return null;
  }

  const expires = localStorage.getItem(AUTH_LS_EXPIRES);
  const expiresDate = expires ? new Date(expires) : null;

  return expiresDate && expiresDate <= new Date() ? null : authorization;
};

export const getPersistedState = (): BaseAuthState => {
  const authorization = getStorage(AUTH_COOKIE_NAME) || null;
  const expires = localStorage.getItem(AUTH_LS_EXPIRES);
  const refreshToken = localStorage.getItem(REFRESH_LS_NAME);

  if (!authorization) {
    clearState();
    return emptyState;
  }

  const expiresDate = expires ? new Date(expires) : null;
  const isTokenExpired = expiresDate && expiresDate <= new Date();

  if (isTokenExpired && authorization) {
    clearState();
    return emptyState;
  }

  return {
    authorization,
    expires: expiresDate,
    signedIn: !!authorization && !isTokenExpired,
    refreshToken,
    userId: authorization ? extractUserIdFromAuth(authorization) : null,
  };
};

const updateTokens: () => AuthHandler<Signin | RefreshTokens> = () => (_state, payload) => {
  const { accessToken, accessTokenExpiresAt, refreshToken } = payload;

  const userId = accessToken ? extractUserIdFromToken(accessToken) : null;

  const newState = {
    authorization: `Bearer ${accessToken}`,
    expires: accessTokenExpiresAt ? new Date(accessTokenExpiresAt) : null,
    signedIn: true,
    refreshToken,
    initialized: true,
    userId,
    signoutTriggerBy: null,
  };

  setState(newState);
  return newState;
};

const handlers = {
  [AuthActionTypes.SIGNIN]: updateTokens(),
  [AuthActionTypes.REFRESH_TOKENS]: updateTokens(),
  [AuthActionTypes.SIGNOUT]: (_state: AuthState, payload: { triggeredBy?: 'user' | 'system' }): AuthState => {
    clearState();
    return {
      ...emptyState,
      initialized: true,
      signoutTriggerBy: payload?.triggeredBy || 'system',
    };
  },
};

const persistedState = getPersistedState();
export const initialAuthState: AuthState = {
  ...persistedState,
  // If we're logged in we will check the token still works on load, while we perform this check "initialized = false"
  // If we're not logged in there is no token for us to check so "initialized = true" immediately
  initialized: !persistedState.signedIn,
  signoutTriggerBy: null,
};
export const authReducer = createReducer<AuthState>(handlers);
